import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { TrendingUp, Sparkles, Globe, Languages, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { InvestModal } from '../components/InvestLanguageFlags';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Clean names used in the invest modal title.
const NAME = {
  lang_lt: 'Lietuviešu', lang_ee: 'Igauņu', region_lt: 'Lietuva', region_ee: 'Igaunija',
};

export default function InvestPage() {
  const { language } = useLanguage();
  const [p, setP] = useState(null);
  const [target, setTarget] = useState(null);
  const isLV = language === 'lv';
  const L = (lv, en, ru) => (language === 'lv' ? lv : language === 'ru' ? (ru ?? en) : en);

  const load = () => axios.get(`${API}/investments/progress`).then(({ data }) => setP(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const eur = (n) => new Intl.NumberFormat('lv-LV').format(Math.round(n || 0));
  if (!p) return <div className="min-h-[60vh] flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#D97736]" /></div>;

  const entries = Object.entries(p.campaigns || {});
  const languages = entries.filter(([, c]) => c.type === 'language');
  const regions = entries.filter(([, c]) => c.type === 'region');

  const Card = ({ ckey, c }) => {
    const collected = p.per_campaign?.[ckey]?.total || 0;
    const count = p.per_campaign?.[ckey]?.count || 0;
    return (
      <div className="bg-white rounded-2xl border border-[#E5E4E0] p-5 flex flex-col" data-testid={`invest-card-${ckey}`}>
        <div className="flex items-center gap-2 mb-1">
          {c.type === 'language' ? <Languages className="w-4 h-4 text-[#4A6B53]" /> : <Globe className="w-4 h-4 text-[#D97736]" />}
          <h3 className="font-semibold text-[#121212]">{isLV ? c.label_lv : c.label_en}</h3>
        </div>
        <p className="text-sm text-[#5C5C5C] mb-4">
          {isLV ? 'Savākts' : language === 'ru' ? 'Собрано' : 'Raised'}: <strong className="text-[#4A6B53]">€{eur(collected)}</strong>
          {count > 0 && <span className="text-[#9CA3AF]"> · {count} {L('ieguldījumi', 'contributions', 'вкладов')}</span>}
        </p>
        <button
          onClick={() => setTarget({ campaign: ckey, name: NAME[ckey] || (isLV ? c.label_lv : c.label_en) })}
          className="mt-auto w-full h-11 rounded-full bg-[#D97736] hover:bg-[#C06528] text-white font-semibold text-sm transition-colors"
          data-testid={`invest-btn-${ckey}`}
        >
          {L('Investēt', 'Invest', 'Инвестировать')}
        </button>
      </div>
    );
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="invest-page">
      <Link to="/" className="inline-flex items-center gap-1.5 text-[#5C5C5C] hover:text-[#D97736] text-sm font-medium mb-6 transition-colors">
        <ArrowLeft className="w-4 h-4" /> {L('Uz sākumu', 'Home', 'На главную')}
      </Link>

      <div className="text-center mb-8">
        <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.2em] text-[#D97736] mb-3">
          <Sparkles className="w-4 h-4" /> {L('Kļūsti par investoru', 'Become an investor', 'Станьте инвестором')}
        </span>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-[#121212] font-outfit">
          {L('Investē KoLV nākotnē', 'Invest in KoLV\u2019s future', 'Инвестируйте в будущее KoLV')}
        </h1>
        <p className="text-[#5C5C5C] mt-3 max-w-2xl mx-auto">
          {isLV
            ? <>Atbalsti jaunas valodas un valstis KoLV kartē. <strong className="text-[#D97736]">{p.profit_share_pct}% no KoLV peļņas</strong> tiek sadalīti proporcionāli starp visiem investoriem — jo lielāks ieguldījums, jo lielāka daļa.</>
            : language === 'ru'
            ? <>Поддержите новые языки и страны на карте KoLV. <strong className="text-[#D97736]">{p.profit_share_pct}% прибыли KoLV</strong> распределяются пропорционально между всеми инвесторами.</>
            : <>Support new languages and countries on the KoLV map. <strong className="text-[#D97736]">{p.profit_share_pct}% of KoLV profit</strong> is shared proportionally among investors.</>}
        </p>
        <Link to="/investiciju-noteikumi" className="inline-block mt-3 text-sm font-medium text-[#D97736] hover:text-[#C06528] underline underline-offset-2" data-testid="invest-terms-link">
          {L('Investīciju un peļņas sadales noteikumi', 'Investment & profit distribution terms', 'Условия инвестиций и распределения прибыли')}
        </Link>
      </div>

      {/* Global progress */}
      <div className="bg-white rounded-2xl border border-[#E5E4E0] p-6 mb-10">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <span className="flex items-center gap-1.5 font-semibold text-[#121212]">
            <TrendingUp className="w-4 h-4 text-[#D97736]" /> {L('Kopējais mērķis', 'Overall goal', 'Общая цель')}
          </span>
          <span className="text-sm text-[#5C5C5C]">
            <strong className="text-[#4A6B53] text-lg">€{eur(p.collected)}</strong> / €{eur(p.goal)}
            <span className="mx-2 text-[#E5E4E0]">·</span>
            <strong className="text-[#121212]">{p.backers_count}</strong> {L('investori', 'investors', 'инвесторов')}
          </span>
        </div>
        <div className="h-3 rounded-full bg-[#F0EFEB] overflow-hidden">
          <div className="h-full rounded-full bg-gradient-to-r from-[#D97736] to-[#E8A05C] transition-all duration-700" style={{ width: `${p.percent}%` }} data-testid="invest-page-progress" />
        </div>

        <button
          onClick={() => setTarget({ campaign: 'general', name: L('KoLV attīstība', 'KoLV development', 'Развитие KoLV') })}
          className="mt-5 w-full h-12 rounded-full bg-[#4A6B53] hover:bg-[#3D5A45] text-white font-semibold text-sm flex items-center justify-center gap-2 transition-colors"
          data-testid="invest-btn-general"
        >
          <Sparkles className="w-4 h-4" />
          {L('Investē vienkārši KoLV attīstībai (bez īpašas izvēles)', 'Invest simply for KoLV development (no specific choice)', 'Инвестируйте просто в развитие KoLV (без выбора)')}
        </button>
        <p className="text-xs text-[#9CA3AF] text-center mt-2">
          {L('Palīdzi sasniegt 10 000 € mērķi — tavs ieguldījums iet vispārējai platformas attīstībai.',
             'Help reach the €10,000 goal — your contribution goes toward overall platform development.',
             'Помогите достичь цели в 10 000 € — ваш вклад идёт на общее развитие платформы.')}
        </p>
      </div>

      <h2 className="text-lg font-semibold text-[#121212] mb-3 flex items-center gap-2"><Languages className="w-5 h-5 text-[#4A6B53]" /> {L('Valodas', 'Languages', 'Языки')}</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
        {languages.map(([k, c]) => <Card key={k} ckey={k} c={c} />)}
      </div>

      <h2 className="text-lg font-semibold text-[#121212] mb-3 flex items-center gap-2"><Globe className="w-5 h-5 text-[#D97736]" /> {L('Valstis kartē', 'Countries on the map', 'Страны на карте')}</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {regions.map(([k, c]) => <Card key={k} ckey={k} c={c} />)}
      </div>

      <InvestModal target={target} onClose={() => setTarget(null)} />
    </div>
  );
}
